import React from "react"
import { useTranslation } from "react-i18next"
import styles from "../EventDashboardV2.module.scss"

type Intent = "Green" | "Red" | "Orange" | "Default"

interface StatCardProps {
  value: number
  total: number
  label: string
  /**
   * Semantic intent of the metric.
   *   Green  → positive progress (e.g. Sent, Delivered, Accepted)
   *   Red    → problem signal — only colors visual cues when value > 0
   *   Orange → pending / attention — only colors visual cues when value > 0
   *   Default → fully neutral
   *
   * Drives the progress-bar fill color, not the number itself. Numbers stay
   * neutral so the grid reads as a calm dashboard, not a traffic light.
   */
  valueColor?: Intent
  onClick?: () => void
  /**
   * Optional override for the progress-bar fill percentage. When omitted, we
   * derive it from value/total so every card has a consistent bar.
   */
  progress?: number
  /**
   * Optional unit noun for the supporting line, e.g. "guests" (default),
   * "invitations", "messages". Lets callers tune copy without a new prop.
   */
  unit?: string

  /**
   * Label for the top-right click affordance. Rendered only when the card is
   * clickable. Defaults to "View" so every interactive card advertises itself
   * consistently; override when a different verb reads better ("Open", etc).
   */
  linkText?: string

  // Legacy props accepted silently for backwards compatibility.
  badgeText?: string
  badgeVariant?: "Green" | "Red" | "Orange"
}

const clamp = (n: number, lo = 0, hi = 100) => Math.min(hi, Math.max(lo, n))

const StatCard: React.FC<StatCardProps> = ({
  value,
  total,
  label,
  valueColor = "Default",
  onClick,
  progress,
  unit: _unit,
  linkText,
}) => {
  const { t } = useTranslation()
  const resolvedLinkText = linkText ?? t("eventDashboardV2.actions.view")
  const hasTotal = total > 0
  const derivedPercent = hasTotal ? Math.round((value / total) * 100) : 0
  const percent =
    progress !== undefined ? clamp(Math.round(progress)) : derivedPercent

  // Only surface color when the metric earns it: green stays green only past
  // zero, red/orange only signal when there's something to worry about.
  // A "0 Failed" card should feel calm, not alarming.
  const activeIntent: Intent = value > 0 ? valueColor : "Default"

  const cardClass = [
    styles.statCard,
    onClick ? styles.statCardClickable : "",
  ]
    .filter(Boolean)
    .join(" ")

  const fillClass = [
    styles.statCardProgressFill,
    styles[`statCardProgressFill${activeIntent}`],
  ].join(" ")

  return (
    <div className={cardClass} onClick={onClick}>
      <div className={styles.statCardHeader}>
        <p className={styles.statCardEyebrow}>{label}</p>
        {onClick && (
          <span className={styles.statCardAction} aria-hidden="true">
            {resolvedLinkText}
            <svg
              width="6"
              height="6"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.5 2.5L8 6L4.5 9.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </div>

      <div className={styles.statCardValueRow}>
        <span className={styles.statCardNumber}>{value}</span>
      </div>

      <div className={styles.statCardSubline}>
        <span>
          {t("eventDashboardV2.statCard.ofTotalGuests", { count: hasTotal ? total : 0 })}
        </span>
        {hasTotal && (
          <span className={styles.statCardPercent}>{percent}%</span>
        )}
      </div>

      <div className={styles.statCardProgress} aria-hidden="true">
        <div className={fillClass} style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}

export default StatCard
